"use client";

import React from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAction } from "next-safe-action/hooks";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Navigation } from "swiper/modules";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Paragraph } from "../ui/typography";
import ContinueWatch from "../card/continue-watch";
import SwiperNav from "../swiper-nav";

import { fetchContinuePreparationList } from "@/fetchers/order";
import { getParentsForMultipleCourses } from "@/fetchers/course-consuption";

import { isAuthenticatedAtom } from "@/store/auth";
import { showOpenInAppModalAtom } from "@/store/modal";

import usePlatformDetector from "@/hooks/use-platform-detector";

import { cn, convertToSlug } from "@/lib/utils";

function ConitunePreparation({ className, ...props }) {
  const testWindowRef = React.useRef();

  const navPrevRef = React.useRef(null);
  const navNextRef = React.useRef(null);

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const showOpenInAppModal = useSetAtom(showOpenInAppModalAtom);

  const [swiper, setSwiper] = React.useState(null);

  const { isMobile } = usePlatformDetector();

  const {
    isExecuting: fetchingList,
    result: { data: coursesList } = {},
    execute: fetchList,
  } = useAction(fetchContinuePreparationList);

  React.useEffect(() => {
    if (!isAuthenticated) return;

    fetchList();
  }, [isAuthenticated, fetchList]);

  if (!isAuthenticated) return null;

  const createVideoDashboardRoute = (course) => {
    const parentPath = course?.parentObject
      ?.map((parent) => parent.id)
      .join("/");
    let route = `/course-content/${course.courseId}-${course.languageId}/`;
    route += parentPath.replace(/\d+$/, "");
    route += `${course.lessonId}/${convertToSlug(course.lessonName)}`;
    sessionStorage.setItem("sourceURL", "home");
    return route;
  };

  const handleOnCourseClick = (course) => async () => {
    if (isMobile) {
      showOpenInAppModal(true);
      return;
    }

    // Handle quiz course type
    if (course.contentType.toLowerCase() === "quiz") {
      const { testStatus, courseId, mappingId } = course;
      if (testStatus === "START_NOW" || testStatus === "INCOMPLETE") {
        testWindowRef.current = window.open(
          `/test-series/${courseId || 1}/${mappingId}`,
          "_blank",
          "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no"
        );
      } else if (testStatus === "COMPLETED") {
        testWindowRef.current = window.open(
          `/test-series/${courseId}/${mappingId}/result`,
          "_blank",
          "directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no"
        );
      }

      return;
    }

    const { courseId, languageId, lessonId } = course;
    const response = await getParentsForMultipleCourses([
      {
        courseId,
        languageId,
        lessonId,
      },
    ]);

    const parentObject = response.data[`${courseId}-${lessonId}`];
    window.location.href = createVideoDashboardRoute({
      ...course,
      parentObject,
    });
  };

  // Return null if there are no courses to continue preparation
  if (!fetchingList && coursesList?.length === 0) return null;

  return (
    <Card
      className={cn("container-studyiq pt-0 pb-6 gap-y-4", className)}
      {...props}
    >
      <CardHeader className="rounded-t-lg md:shadow-card">
        <CardTitle>
          <Paragraph variant="b2">Continue your preparation</Paragraph>
        </CardTitle>
      </CardHeader>

      <CardContent className="relative px-0 md:px-3">
        {!fetchingList && coursesList && (
          <Swiper
            modules={[Mousewheel, Navigation]}
            spaceBetween={12}
            slidesPerView="auto"
            slidesOffsetBefore={10}
            slidesOffsetAfter={10}
            mousewheel={{ forceToAxis: true }}
            onSwiper={setSwiper}
          >
            {coursesList?.map((course) => (
              <SwiperSlide key={course.lessonId} className="w-37! py-1">
                <ContinueWatch
                  course={course}
                  onClick={handleOnCourseClick(course)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        <SwiperNav
          swiper={swiper}
          nextNavRef={navNextRef}
          prevNavRef={navPrevRef}
          className="hidden md:flex absolute top-1/2 left-0 -translate-y-1/2 w-full justify-between z-10"
          iconClassName="data-[nav=prev]:-translate-x-full data-[nav=next]:translate-x-full"
        />
      </CardContent>
    </Card>
  );
}

export default ConitunePreparation;
